import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import styled from 'styled-components';
import axios from 'axios';
import instance from "src/API/MainAPI";

// 스타일 컴포넌트는 기존 코드와 동일
const UploadProgressModal = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 300px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 15px;
  z-index: 1000;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ModalTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9f9f9f;
`;

const ProgressContainer = styled.div`
  margin-bottom: 10px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
`;

const Progress = styled.div<{ width: number }>`
  height: 100%;
  width: ${props => props.width}%;
  background-color: #2282e9;
  transition: width 0.3s ease;
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #9f9f9f;
  margin-top: 5px;
`;

// 파일 타입 정의
interface UploadFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  message?: string;
}

// 저장된 업로드 정보 타입
interface SavedUploadInfo {
  fileName: string;
  fileSize: number;
  progress: number;
  status: string;
  message?: string;
}

// 전역 이벤트 이름 정의
const UPLOAD_EVENT = 'FILE_UPLOAD_UPDATED';
const UPLOAD_STORAGE_KEY = 'fileUploads';

// S3 업로드 함수 (분리하여 재사용)
export const uploadFileToS3 = async (file: File, gameProposalId: string | number, fileCategory: string, entityType: string) => {
  try {
    // 현재 업로드 상태 가져오기
    const currentUploads = getUploadState();
    const fileIndex = currentUploads.findIndex(upload => 
      upload.fileName === file.name && upload.fileSize === file.size
    );
    
    if (fileIndex === -1) return; // 파일 정보가 없으면 중단
    
    // 업로드 상태 업데이트 - 시작
    updateUploadState(fileIndex, { status: 'uploading' });
    
    // Presigned URL 요청
    const presignedUrl = await getPresignedUrl(file.name, file.type, gameProposalId, fileCategory, entityType);
    
    // S3에 직접 업로드
    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (progressEvent) => {
        // 진행 상황 계산 및 상태 업데이트
        const percentCompleted = progressEvent.total 
          ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
          : 0;
          
        updateUploadState(fileIndex, { progress: percentCompleted });
      },
    });
    
    // 업로드 성공 처리
    updateUploadState(fileIndex, { progress: 100, status: 'completed' });
    
  } catch (error) {
    console.error('Failed to upload file to S3:', error);
    
    // 업로드 실패 상태 업데이트
    const currentUploads = getUploadState();
    const fileIndex = currentUploads.findIndex(upload => 
      upload.fileName === file.name && upload.fileSize === file.size
    );
    
    if (fileIndex !== -1) {
      updateUploadState(fileIndex, { 
        status: 'error', 
        message: '업로드 실패' 
      });
    }
  }
};

// Presigned URL 요청 함수
export const getPresignedUrl = async (fileName: string, fileType: string, gameProposalId: string | number, fileCategory: string, entityType: string): Promise<string> => {
  try {
    const response = await instance.post('/v1/files/pre-signed-url', {
      "entityId": gameProposalId,
      "entityType": entityType,
      "category": fileCategory,
      "originalFilename": fileName,
      "visible": true
    });
    
    return response.data.preSignedURL;
  } catch (error) {
    console.error('Failed to get presigned URL:', error);
    throw error;
  }
};

// 업로드 상태 업데이트 함수
export const updateUploadState = (fileIndex: number, updateData: Partial<SavedUploadInfo>) => {
  const currentUploads = getUploadState();
  
  if (fileIndex >= 0 && fileIndex < currentUploads.length) {
    const updatedUploads = [...currentUploads];
    updatedUploads[fileIndex] = { ...updatedUploads[fileIndex], ...updateData };
    
    localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(updatedUploads));
    
    // 전역 이벤트 발생
    window.dispatchEvent(new CustomEvent(UPLOAD_EVENT));
  }
};

// 업로드 상태 가져오기
export const getUploadState = (): SavedUploadInfo[] => {
  try {
    const savedUploads = localStorage.getItem(UPLOAD_STORAGE_KEY);
    return savedUploads ? JSON.parse(savedUploads) : [];
  } catch (e) {
    console.error('Failed to parse saved uploads', e);
    return [];
  }
};

// 업로드 상태 추가 함수
export const addUploadState = (files: File[]) => {
  const currentUploads = getUploadState();
  
  const newUploads = files.map(file => ({
    fileName: file.name,
    fileSize: file.size,
    progress: 0,
    status: 'pending'
  }));
  
  const updatedUploads = [...currentUploads, ...newUploads];
  localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(updatedUploads));
  
  // 전역 이벤트 발생
  window.dispatchEvent(new CustomEvent(UPLOAD_EVENT));
  
  return updatedUploads;
};

// 업로드 상태 제거 함수
export const removeUploadState = (fileIndex: number) => {
  const currentUploads = getUploadState();
  
  if (fileIndex >= 0 && fileIndex < currentUploads.length) {
    const updatedUploads = currentUploads.filter((_, i) => i !== fileIndex);
    localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(updatedUploads));
    
    // 전역 이벤트 발생
    window.dispatchEvent(new CustomEvent(UPLOAD_EVENT));
    
    return updatedUploads;
  }
  
  return currentUploads;
};

// 파일 크기 포맷팅 함수
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 총 업로드 진행률 계산
export const calculateTotalProgress = (uploads: SavedUploadInfo[]): number => {
  if (uploads.length === 0) return 0;
  
  const totalProgress = uploads.reduce((sum, file) => sum + file.progress, 0);
  return Math.round(totalProgress / uploads.length);
};

// 실제 모달 컴포넌트
const UploadProgressPortal: React.FC<{ gameProposalId: string | number }> = ({ gameProposalId }) => {
  const [uploadInfos, setUploadInfos] = useState<SavedUploadInfo[]>([]);
  const [show, setShow] = useState(false);
  
  // 상태 변경 이벤트 리스너 및 새로고침 처리
  useEffect(() => {
    // 이전 세션에서의 업로드 중단 여부 확인
    const wasRefreshed = sessionStorage.getItem('wasRefreshed') === 'true';
    
    if (wasRefreshed) {
      // 이전에 새로고침된 경우, 로컬 스토리지 비우고 모달 닫기
      localStorage.removeItem(UPLOAD_STORAGE_KEY);
      setUploadInfos([]);
      setShow(false);
      // 새로고침 상태 초기화
      sessionStorage.removeItem('wasRefreshed');
    } else {
      // 정상적인 접근인 경우, 현재 업로드 상태 로드
      const currentUploads = getUploadState();
      setUploadInfos(currentUploads);
      setShow(currentUploads.length > 0);
    }
    
    const updateUploadInfos = () => {
      const uploads = getUploadState();
      setUploadInfos(uploads);
      setShow(uploads.length > 0);
    };
    
    // 이벤트 리스너 등록
    window.addEventListener(UPLOAD_EVENT, updateUploadInfos);
    
    // 브라우저 새로고침/닫기 이벤트 리스너 등록
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const currentUploads = getUploadState();
      const hasIncompleteUploads = currentUploads.some(
        file => file.status !== 'completed' && file.status !== 'error'
      );
      
      if (hasIncompleteUploads && show) {
        // 새로고침 감지 표시
        sessionStorage.setItem('wasRefreshed', 'true');
        
        // 표준 브라우저 경고 표시
        e.preventDefault();
        e.returnValue = '업로드가 진행 중입니다. 페이지를 떠나면 업로드가 중단됩니다.';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener(UPLOAD_EVENT, updateUploadInfos);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [show]);
  
  // 모달 닫기 핸들러
  const handleCloseModal = () => {
    const hasIncompleteUploads = uploadInfos.some(
      file => file.status !== 'completed' && file.status !== 'error'
    );
    
    if (!hasIncompleteUploads) {
      setShow(false);
      localStorage.removeItem(UPLOAD_STORAGE_KEY);
    } else {
      // 사용자에게 경고 메시지 표시
      if (window.confirm('업로드가 진행 중입니다. 정말로 창을 닫으시겠습니까?')) {
        setShow(false);
        localStorage.removeItem(UPLOAD_STORAGE_KEY);
      }
    }
  };
  
  // 재시도 핸들러
  const handleRetry = async () => {
    const currentUploads = getUploadState();
    
    // 각 파일에 대해 파일 객체가 없으므로 파일 선택 대화상자 필요
    const errorFiles = currentUploads.filter(info => info.status === 'error');
    
    if (errorFiles.length > 0) {
      alert('재시도할 파일을 다시 선택해주세요.');
      
      // 파일 입력 생성 및 클릭
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.apk,.zip';
      fileInput.style.display = 'none';
      fileInput.multiple = true;
      
      // 파일 선택 이벤트 처리
      fileInput.onchange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          const selectedFiles = Array.from(target.files);
          
          // 실패한 파일과 일치하는 파일만 다시 업로드
          selectedFiles.forEach(file => {
            const matchingErrorFile = errorFiles.find(
              errorFile => errorFile.fileName === file.name && errorFile.fileSize === file.size
            );
            
            if (matchingErrorFile) {
              const fileIndex = currentUploads.findIndex(
                u => u.fileName === file.name && u.fileSize === file.size
              );
              
              if (fileIndex !== -1) {
                // 상태 업데이트
                updateUploadState(fileIndex, { status: 'pending', progress: 0 });
                
                // 업로드 시작
                // uploadFileToS3(file, gameProposalId);
              }
            }
          });
        }
      };
      
      // DOM에 추가하고 클릭 이벤트 발생
      document.body.appendChild(fileInput);
      fileInput.click();
      
      // 사용 후 DOM에서 제거
      setTimeout(() => {
        document.body.removeChild(fileInput);
      }, 500);
    }
  };
  
  // 모달이 표시되지 않으면 null 반환
  if (!show) return null;
  
  // Portal을 통해 body에 직접 렌더링
  return ReactDOM.createPortal(
    <UploadProgressModal>
      <ModalHeader>
        <ModalTitle>파일 업로드 진행 중</ModalTitle>
        <CloseButton onClick={handleCloseModal}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </ModalHeader>
      
      <ProgressContainer>
        <ProgressBar>
          <Progress width={calculateTotalProgress(uploadInfos)} />
        </ProgressBar>
        <ProgressInfo>
          <span>{calculateTotalProgress(uploadInfos)}% 완료</span>
          <span>
            {uploadInfos.filter(f => f.status === 'completed').length}/
            {uploadInfos.length} 파일
          </span>
        </ProgressInfo>
      </ProgressContainer>
      
      {uploadInfos.map((file, index) => (
        <ProgressContainer key={index}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ 
              fontSize: '12px', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap', 
              maxWidth: '200px',
              color: file.status === 'error' ? '#ff6b6b' : 'inherit'
            }}>
              {file.fileName}
            </span>
            <span style={{ 
              fontSize: '12px', 
              color: file.status === 'error' ? '#ff6b6b' : '#9f9f9f' 
            }}>
              {file.status === 'error' ? '실패' : `${Math.round(file.progress)}%`}
            </span>
          </div>
          <ProgressBar>
            <Progress 
              width={file.progress} 
              style={{ backgroundColor: file.status === 'error' ? '#ff6b6b' : '#2282e9' }}
            />
          </ProgressBar>
          {file.status === 'error' && (
            <div style={{ color: '#ff6b6b', fontSize: '12px', marginTop: '5px' }}>
              {file.message || '업로드 실패. 다시 시도해주세요.'}
            </div>
          )}
        </ProgressContainer>
      ))}
      
      {/* 모든 파일 업로드 완료 시 완료 버튼 */}
      {uploadInfos.every(file => file.status === 'completed' || file.status === 'error') && (
        <button 
          style={{
            width: '100%',
            padding: '8px',
            backgroundColor: '#2282e9',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            marginTop: '10px',
            cursor: 'pointer'
          }}
          onClick={() => {
            setShow(false);
            localStorage.removeItem(UPLOAD_STORAGE_KEY);
          }}
        >
          완료
        </button>
      )}
      
      {/* 에러가 있는 파일이 있을 경우 재시도 버튼 */}
      {uploadInfos.some(file => file.status === 'error') && (
        <button 
          style={{
            width: '100%',
            padding: '8px',
            backgroundColor: '#ff6b6b',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            marginTop: '10px',
            cursor: 'pointer'
          }}
          onClick={handleRetry}
        >
          재시도
        </button>
      )}
    </UploadProgressModal>,
    document.body
  );
};

export default UploadProgressPortal; 